import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertTriangle } from "lucide-react";

// Duplicate guard: warns (never blocks) when an email / phone already belongs to another client.
// The check runs on the server so it cannot be bypassed from the browser.
export function useDuplicateCheck({ email, phone, excludeId, enabled = true }) {
  const [matches, setMatches] = useState([]);
  useEffect(() => {
    const em = (email || "").trim();
    const ph = (phone || "").trim();
    if (!enabled || (!em.includes("@") && ph.replace(/\D/g, "").length < 7)) { setMatches([]); return; }
    let live = true;
    const t = setTimeout(() => {
      api.get("/clients/duplicate-check", { params: { email: em || undefined, phone: ph || undefined, exclude_id: excludeId || undefined } })
        .then((r) => { if (live) setMatches(r.data?.matches || []); })
        .catch(() => { if (live) setMatches([]); });
    }, 450);
    return () => { live = false; clearTimeout(t); };
  }, [email, phone, excludeId, enabled]);
  return matches;
}

export function DuplicateWarning({ matches, testId = "duplicate-warning" }) {
  if (!matches?.length) return null;
  const seen = new Set();
  const rows = matches.filter((m) => { const k = `${m.field}:${m.client_id}`; if (seen.has(k)) return false; seen.add(k); return true; });
  return (
    <div data-testid={testId} className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 text-[12px] text-amber-900">
      <div className="flex items-center gap-1.5 font-semibold">
        <AlertTriangle className="h-3.5 w-3.5" /> Possible duplicate — you can still save
      </div>
      <ul className="mt-1 space-y-0.5">
        {rows.map((m) => (
          <li key={`${m.field}:${m.client_id}`} data-testid={`duplicate-match-${m.field}`}>
            This {m.label} is already on{" "}
            <Link to={`/admin/clients/${m.client_id}`} target="_blank" rel="noreferrer"
              data-testid={`duplicate-match-link-${m.client_id}`} className="font-semibold underline">
              {m.full_name || "another client"}
            </Link>
            {m.value ? ` (${m.value})` : ""}{m.active === false ? " · disabled" : ""}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Convenience wrapper for forms that only need the banner.
export function DuplicateGuard({ email, phone, excludeId, enabled = true, testId }) {
  const matches = useDuplicateCheck({ email, phone, excludeId, enabled });
  return <DuplicateWarning matches={matches} testId={testId} />;
}
